import { useEffect, useState } from "react";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import {
  AdAvatar,
  Callout,
  Drawer,
  Empty,
  Field,
  KeyValue,
  Pill,
  Section,
  ToastProvider,
  errText,
  fmtDateTime,
  useToast,
} from "../admin/ui";
import { api, type AdminRole, type AdminUserRow, type Me } from "../services/api";

/** The two iQuest staff roles. Both see every tenant, which is why only an
 *  iQuest admin can assign them — enforced server-side as well. */
const STAFF_ROLES: { value: AdminRole; label: string; blurb: string }[] = [
  {
    value: "iquest_admin",
    label: "iQuest admin",
    blurb: "Full access, creates customers and staff",
  },
  {
    value: "service_desk",
    label: "Service desk",
    blurb: "Alerts, forms, KB and view-as",
  },
];

function roleLabel(role: string): string {
  return STAFF_ROLES.find((r) => r.value === role)?.label ?? role;
}

/**
 * iQuest staff.
 *
 * People at iQuest who can see every customer. Client-side users are managed
 * from their own customer's page, which is the tenant boundary: this page is
 * iQuest-admin only because everything it creates reads across tenants.
 */
export function AdminUsers() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  if (!me) return <div className="card">Loading…</div>;

  if (me.role !== "iquest_admin") {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
        <p className="empty-state-blurb">
          Managing iQuest staff accounts is reserved for iQuest admins. To manage your own
          people, open your organisation under Customers.
        </p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <AdminUsersInner me={me} />
    </ToastProvider>
  );
}

function AdminUsersInner({ me }: { me: Me }) {
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [adding, setAdding] = useState(false);

  const { data, isLoading, mutate, error } = useSWR(
    ["/api/admin/users/staff", search, showInactive],
    () =>
      api.admin.users.list({
        q: search.trim() || undefined,
        staff_only: true,
        include_inactive: showInactive,
        page: 1,
        page_size: 100,
      }),
    { keepPreviousData: true },
  );

  const items = data?.items ?? [];

  return (
    <div className="rf-wrap">
      <div className="ad-head">
        <div>
          <div className="ad-eyebrow">Admin</div>
          <h1 className="rf-h1">iQuest staff</h1>
          <p className="rf-sub">
            People at iQuest who can see every customer. Client-side users are managed from
            each customer&apos;s page.
          </p>
        </div>
        <div className="grp">
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            <Icon name="userPlus" size={15} /> Add staff member
          </button>
        </div>
      </div>

      <div className="ad-filters">
        <div className="rf-search">
          <Icon name="search" />
          <input
            type="search"
            value={search}
            placeholder="Search name or email"
            aria-label="Search staff"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="checkbox-row" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Show deactivated</span>
        </label>
      </div>

      <div className="ad-card">
        {error ? (
          <div className="form-error" style={{ margin: 18 }}>
            {String((error as Error).message ?? error)}
          </div>
        ) : isLoading && !data ? (
          <div style={{ padding: 18 }}>Loading staff…</div>
        ) : items.length === 0 ? (
          <Empty title="No staff found" blurb="Try a different search." />
        ) : (
          <div className="ad-tablewrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Last sign-in</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr
                    key={u.id}
                    className="clickable"
                    style={u.status === "active" ? undefined : { opacity: 0.6 }}
                    onClick={() => setEditing(u)}
                  >
                    <td>
                      <span className="who">
                        <AdAvatar name={u.display_name || u.email} />
                        <span>
                          <span className="n">
                            {u.display_name}
                            {u.id === me.id && (
                              <span className="ad-scope" style={{ marginLeft: 6 }}>
                                You
                              </span>
                            )}
                          </span>
                          <br />
                          <span className="e">{u.email}</span>
                        </span>
                      </span>
                    </td>
                    <td className="nowrap">{roleLabel(u.role)}</td>
                    <td className="mono nowrap">{fmtDateTime(u.last_login_at)}</td>
                    <td className="nowrap">
                      {u.status === "active" ? (
                        <Pill kind="ok">Active</Pill>
                      ) : (
                        <Pill kind="off">
                          {u.status === "removed" ? "Deleted" : "Deactivated"}
                        </Pill>
                      )}
                    </td>
                    <td className="r">
                      <span style={{ color: "var(--iq-text-disabled)", display: "inline-flex" }}>
                        <Icon name="chevRight" size={16} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <Section title="How the two roles differ">
          <KeyValue
            k="iQuest admin"
            v="Everything: customers, staff, forms, status alerts, knowledge base, view-as."
          />
          <KeyValue
            k="Service desk"
            v="Status alerts, forms, knowledge base and view-as. Cannot create customers or staff."
          />
        </Section>
      </div>

      {adding && (
        <StaffDrawer
          me={me}
          user={null}
          onClose={() => setAdding(false)}
          onChanged={async () => {
            setAdding(false);
            await mutate();
          }}
        />
      )}
      {editing && (
        <StaffDrawer
          me={me}
          user={editing}
          onClose={() => setEditing(null)}
          onChanged={async () => {
            setEditing(null);
            await mutate();
          }}
        />
      )}
    </div>
  );
}

function StaffDrawer({
  me,
  user,
  onClose,
  onChanged,
}: {
  me: Me;
  user: AdminUserRow | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const isNew = user === null;
  const isSelf = user?.id === me.id;
  const inactive = user != null && user.status !== "active";

  const [name, setName] = useState(user?.display_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [role, setRole] = useState<AdminRole>((user?.role as AdminRole) ?? "service_desk");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(user?.display_name ?? "");
    setEmail(user?.email ?? "");
    setRole((user?.role as AdminRole) ?? "service_desk");
  }, [user]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        if (!name.trim() || !email.trim()) {
          setError("Name and email are both required.");
          setBusy(false);
          return;
        }
        await api.admin.users.create({
          email: email.trim(),
          display_name: name.trim(),
          role,
          // Staff belong to the iQuest tenant, which is whichever company the
          // signed-in admin is in.
          company_id: me.company_id,
        });
        toast(`Added ${name.trim()}`);
      } else {
        if (role !== user.role) {
          await api.admin.users.setRole(user.id, role);
          toast(`Saved ${user.display_name}`);
        }
      }
      await onChanged();
    } catch (e) {
      setError(errText(e, "Could not save."));
      setBusy(false);
    }
  }

  async function toggleStatus() {
    if (!user) return;
    const next = inactive ? "active" : "suspended";
    if (
      next === "suspended" &&
      !window.confirm(
        `Deactivate ${user.display_name}? They lose access to every tenant immediately.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.admin.users.setStatus(user.id, next);
      toast(
        next === "active"
          ? `${user.display_name} reactivated`
          : `${user.display_name} deactivated`,
      );
      await onChanged();
    } catch (e) {
      setError(errText(e, "Could not update this account."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow="iQuest staff"
      title={isNew ? "Add staff member" : (user as AdminUserRow).display_name}
      desc={
        isNew
          ? "They sign in with their Microsoft work account; the portal matches on email."
          : (user as AdminUserRow).email
      }
      onClose={onClose}
      footer={
        <>
          {!isNew && !isSelf && (
            <button
              type="button"
              className={`btn btn-sm left ${inactive ? "btn-ghost" : "btn-danger"}`}
              onClick={() => void toggleStatus()}
              disabled={busy}
            >
              {inactive ? "Reactivate" : "Deactivate"}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={busy || (!isNew && isSelf)}
          >
            {busy ? "Saving…" : isNew ? "Add" : "Save"}
          </button>
        </>
      }
    >
      {isSelf && (
        <div style={{ marginBottom: 14 }}>
          <Callout kind="warn">
            You cannot change your own role or deactivate yourself. Ask another iQuest
            admin.
          </Callout>
        </div>
      )}

      <div className="ad-grid">
        <Field
          label="Full name"
          note={isNew ? undefined : "Set from their Microsoft profile at sign-in."}
        >
          <input
            value={name}
            maxLength={200}
            readOnly={!isNew}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Email" note={isNew ? "Must be an iQuest account." : undefined}>
          <input
            type="email"
            value={email}
            maxLength={320}
            readOnly={!isNew}
            placeholder="name@iquest.com.au"
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className="ad-field span2">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>Role</label>
          <div className="ad-sev" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {STAFF_ROLES.map((r) => (
              <label key={r.value}>
                <input
                  type="radio"
                  name="staff-role"
                  value={r.value}
                  checked={role === r.value}
                  disabled={isSelf}
                  onChange={() => setRole(r.value)}
                />
                <span className="t">{r.label}</span>
                <span className="s">{r.blurb}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}
